/**
 * Nucery Quick Add — shared variant-picker popup.
 *
 * When a product card's bag/ADD icon is clicked and the product has options to
 * choose, open a popup (desktop modal / mobile bottom sheet) to pick the variant,
 * then add it to the cart and open the Nucery cart drawer. Single-variant cards
 * keep their existing instant add (we only intercept when the trigger declares
 * data-variant-count > 1).
 *
 * Trigger contract (any surface): an element matching one of TRIGGER_SELECTORS
 * carrying  data-variant-count="N"  and (ideally) data-product-handle="...".
 * If the handle attribute is missing we derive it from the nearest
 * a[href*="/products/"] inside the card.
 */
(function () {
  'use strict';

  var TRIGGER_SELECTORS = '[data-nucery-quick-add],[data-nqa-trigger],[data-cfs-add]';
  var modal, bodyEl, productCache = {};
  var current = { product: null, selected: [] };

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- money (active presentment currency, like the cart drawer) ---------- */
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.code) || 'USD';
  }
  function shopCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.code) || activeCurrency();
  }
  function activeRate() {
    var r = (window.Shopify && window.Shopify.currency) ? parseFloat(window.Shopify.currency.rate) : NaN;
    return (isFinite(r) && r > 0) ? r : 1;
  }
  function formatMoney(cents) {
    cents = Number(cents) || 0;
    var active = String(activeCurrency());
    var shop = String(shopCurrency());
    var amount = (active.toUpperCase() === shop.toUpperCase()) ? cents : cents * activeRate();
    var fmt = (window.theme && window.theme.moneyFormat) || (window.Shopify && window.Shopify.money_format);
    if (active.toUpperCase() === shop.toUpperCase() && window.Shopify && typeof Shopify.formatMoney === 'function' && fmt) {
      var s = Shopify.formatMoney(cents, fmt);
      var d = document.createElement('div'); d.innerHTML = s; return (d.textContent || d.innerText || '').trim();
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(amount / 100);
    } catch (e) {
      return (amount / 100).toFixed(2) + ' ' + active;
    }
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------- modal shell ---------- */
  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.id = 'NuceryQuickAdd';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="nqa__scrim" data-nqa-close></div>' +
      '<div class="nqa__dialog" role="dialog" aria-modal="true" aria-label="Choose options">' +
      '<button type="button" class="nqa__x" data-nqa-close aria-label="Close">×</button>' +
      '<div class="nqa__grab" aria-hidden="true"></div>' +
      '<div class="nqa__body" data-nqa-body></div>' +
      '</div>';
    document.body.appendChild(modal);
    bodyEl = modal.querySelector('[data-nqa-body]');

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-nqa-close]')) { close(); return; }
      var optBtn = e.target.closest('.nqa__opt-btn');
      if (optBtn) { selectOption(optBtn); return; }
      if (e.target.closest('[data-nqa-add]')) { addCurrent(); return; }
    });
    return modal;
  }

  function openModal() {
    ensureModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('nqa-lock');
  }
  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('nqa-lock');
  }

  /* ---------- product option helpers ---------- */
  function optionNames(product) {
    var opts = product.options || [];
    return opts.map(function (o) { return (o && o.name) ? o.name : o; });
  }
  function variantOptions(v) {
    if (v.options && v.options.length) return v.options;
    var out = [];
    if (v.option1 != null) out.push(v.option1);
    if (v.option2 != null) out.push(v.option2);
    if (v.option3 != null) out.push(v.option3);
    return out;
  }
  function valuesForOption(product, idx) {
    var seen = {}, out = [];
    (product.variants || []).forEach(function (v) {
      var val = variantOptions(v)[idx];
      if (val != null && !seen[val]) { seen[val] = 1; out.push(val); }
    });
    return out;
  }
  function firstAvailable(product) {
    var vs = product.variants || [];
    for (var i = 0; i < vs.length; i++) { if (vs[i].available) return vs[i]; }
    return vs[0] || null;
  }
  // Is an option the implicit Shopify default ("Title" → "Default Title")?
  function isDefaultOption(name, vals) {
    return String(name).toLowerCase() === 'title' &&
      vals.length <= 1 && (!vals.length || /default/i.test(String(vals[0])));
  }
  // Does this product have real, named options (Size, Color, …) — even if some
  // only have a single value? Returns false only for the implicit single
  // "Title / Default Title" variant. We open the picker for anything with real
  // options so the shopper can see/confirm the selection (incl. sold-out state).
  function hasRealOptions(product) {
    var names = optionNames(product);
    if (!names.length) return false;
    for (var i = 0; i < names.length; i++) {
      if (!isDefaultOption(names[i], valuesForOption(product, i))) return true;
    }
    return false;
  }
  function resolveVariant(product, selected) {
    return (product.variants || []).filter(function (v) {
      return variantOptions(v).every(function (val, i) {
        return selected[i] == null || String(selected[i]) === String(val);
      });
    })[0] || null;
  }
  // Is `value` at option `idx` available given the OTHER currently-selected options?
  function valueAvailable(product, idx, value, selected) {
    return (product.variants || []).some(function (v) {
      var vo = variantOptions(v);
      if (String(vo[idx]) !== String(value)) return false;
      for (var i = 0; i < selected.length; i++) {
        if (i !== idx && selected[i] != null && String(selected[i]) !== String(vo[i])) return false;
      }
      return v.available;
    });
  }

  /* ---------- render ---------- */
  function featuredUrl(product) {
    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    if (img && img.indexOf('width=') === -1) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=300';
    return img;
  }

  function render() {
    var p = current.product;
    if (!p || !bodyEl) return;
    var names = optionNames(p);
    var optsHtml = names.map(function (name, idx) {
      var vals = valuesForOption(p, idx);
      if (!vals.length || isDefaultOption(name, vals)) return ''; // hide only the implicit "Title" option
      var pills = vals.map(function (val) {
        var sel = String(current.selected[idx]) === String(val) ? ' sel' : '';
        var avail = valueAvailable(p, idx, val, current.selected);
        return '<button type="button" class="nqa__opt-btn' + sel + (avail ? '' : ' soldout') +
          '" data-nqa-opt="' + idx + '" data-nqa-val="' + escapeAttr(val) + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="nqa__opt"><div class="nqa__opt-name">' + escapeHtml(name) + '</div><div class="nqa__opts">' + pills + '</div></div>';
    }).join('');

    var v = resolveVariant(p, current.selected);
    var priceHtml = '';
    if (v) {
      priceHtml = '<div class="nqa__price">' + escapeHtml(formatMoney(v.price));
      if (v.compare_at_price && v.compare_at_price > v.price) priceHtml += ' <s>' + escapeHtml(formatMoney(v.compare_at_price)) + '</s>';
      priceHtml += '</div>';
    }
    var img = featuredUrl(p);
    var canAdd = v && v.available;
    var addLabel = canAdd ? 'Add to cart' : (v ? 'Sold out' : 'Select options');

    bodyEl.innerHTML =
      '<div class="nqa__media">' + (img ? '<img src="' + escapeAttr(img) + '" alt="' + escapeAttr(p.title) + '">' : '') + '</div>' +
      '<div class="nqa__info">' +
        (p.type ? '<div class="nqa__eyebrow">' + escapeHtml(p.type) + '</div>' : '') +
        '<h3 class="nqa__title">' + escapeHtml(p.title) + '</h3>' +
        priceHtml +
        optsHtml +
        '<button type="button" class="nqa__add" data-nqa-add' + (canAdd ? '' : ' disabled') + '>' + escapeHtml(addLabel) + '</button>' +
      '</div>';
  }

  function selectOption(btn) {
    if (btn.classList.contains('soldout')) return;
    var idx = parseInt(btn.getAttribute('data-nqa-opt'), 10);
    var val = btn.getAttribute('data-nqa-val');
    current.selected[idx] = val;
    render();
  }

  // POST a variant to the cart and open the Nucery drawer. fallbackUrl is used
  // only if the request fails outright.
  function addVariant(variantId, fallbackUrl, onError) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (data) {
        close();
        try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data } })); } catch (e) {}
        try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e2) {}
      })
      .catch(function () {
        if (typeof onError === 'function') onError();
        else if (fallbackUrl) window.location.href = fallbackUrl;
      });
  }

  function addCurrent() {
    var p = current.product;
    if (!p) return;
    var v = resolveVariant(p, current.selected);
    if (!v || !v.available) return;
    var addBtn = bodyEl.querySelector('[data-nqa-add]');
    if (addBtn) addBtn.classList.add('is-loading');
    addVariant(v.id, p.url, function () {
      if (addBtn) addBtn.classList.remove('is-loading');
      if (p.url) window.location.href = p.url;
    });
  }

  /* ---------- open by handle ----------
   * decide=true  → fetch the product and act: ONLY a truly optionless product
   *                that's in stock is added straight to cart; anything with real
   *                options (even one value) OR that's sold out opens the picker
   *                so the shopper can see/confirm the selection and availability.
   *                Use this when the caller can't be sure of the variant count
   *                (e.g. predictive search, which doesn't return all variants).
   * decide=false → always open the picker.
   */
  function openByHandle(handle, preselectVariantId, decide) {
    if (!handle) return;
    var url = root() + 'products/' + encodeURIComponent(handle) + '.js';

    var act = function (p) {
      if (decide && !hasRealOptions(p)) {
        // Optionless product: add the single variant if it's buyable, else
        // fall through to open the picker (which shows the sold-out state).
        var only = firstAvailable(p);
        if (only && only.available) { addVariant(only.id, p.url); return; }
      }
      openModal();
      current.product = p;
      var base = preselectVariantId && (p.variants || []).filter(function (v) { return String(v.id) === String(preselectVariantId); })[0];
      base = base || firstAvailable(p);
      current.selected = base ? variantOptions(base).slice() : [];
      render();
    };

    if (productCache[handle]) { act(productCache[handle]); return; }
    // Show the modal with a loading state right away only when we know we'll
    // open it; in decide mode we wait until we know there are options.
    if (!decide) {
      openModal();
      if (bodyEl) bodyEl.innerHTML = '<div class="nqa__info"><h3 class="nqa__title">…</h3></div>';
    }
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) { productCache[handle] = p; act(p); })
      .catch(function () { if (!decide) close(); });
  }

  /* ---------- trigger detection ---------- */
  function handleFromCard(btn) {
    if (btn.getAttribute('data-product-handle')) return btn.getAttribute('data-product-handle');
    var url = btn.getAttribute('data-product-url') || '';
    if (!url) {
      var card = btn.closest('article, li, .grid-product, .bestsellers-card, .nucery-product-rail__card, .ncs-card, [data-product-url]');
      var link = card && card.querySelector('a[href*="/products/"]');
      if (link) url = link.getAttribute('href') || '';
    }
    var m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function bind() {
    if (bind._bound) return;
    bind._bound = true;
    // Capture phase so we can stop the surface's own (bubble-phase) instant-add
    // before it fires — but ONLY when the product has multiple variants.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest(TRIGGER_SELECTORS);
      if (!btn) return;
      var count = parseInt(btn.getAttribute('data-variant-count'), 10) || 0;
      // data-nqa-decide = caller can't trust the variant count (e.g. predictive
      // search), so we always intercept and let the module fetch + decide.
      var decide = btn.hasAttribute('data-nqa-decide');
      if (count <= 1 && !decide) return; // single variant → let the existing instant-add run
      var handle = handleFromCard(btn);
      if (!handle) return; // can't resolve → let existing behavior run
      e.preventDefault();
      e.stopImmediatePropagation();
      openByHandle(handle, btn.getAttribute('data-variant-id'), decide);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  window.NuceryQuickAdd = { open: openByHandle, close: close };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
